import React from 'react';
import type { ReactNode } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import Link from '@atlaskit/link/link';
import { Text } from '@atlaskit/primitives/compiled/text';

import { fg } from '@confluence/feature-gating';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';
import { mergeLiveDocI18n } from '@confluence/live-pages-utils/entry-points/mergeLiveDocI18n.ts';

import { ALLOWED_FLAG_MESSAGES } from './allowedFlagConstants.ts';

// eslint-disable-next-line check-i18n-id-prefix/i18n-id-prefix-with-package-name-defineMessages
const i18nBase = defineMessages({
	duplicatePageTitleHeader: {
		id: 'editor-flags.save.page.exist.content.title',
		description: 'Title for the flag shown when the page with the current title already exists',
		defaultMessage: 'Give your page a unique name',
	},
	duplicateLivePageTitleHeader: {
		id: 'editor-flags.save.live.page.exist.content.title',
		description:
			'Title for the flag shown when the live page with the current title already exists',
		defaultMessage: 'Give your doc a unique name',
	},
	duplicatePageTitleMessage: {
		id: 'editor-flags.save.page.exist.message',
		description: 'Message for the flag shown when the page with the current title already exists',
		defaultMessage:
			'Page names have to be unique within a space. Give your page a different name to publish it.',
	},
	duplicateLivePageTitleMessage: {
		id: 'editor-flags.save.live.page.exist.message',
		description:
			'Message for the flag shown when the live page with the current title already exists',
		defaultMessage: 'Live docs need unique names within a space. Give your doc a different name. ',
	},
	duplicateUpdatePageTitleMessage: {
		id: 'editor-flags.save.update-page.exist.message',
		description:
			'Message for the flag shown when updating the page with the current title already exists',
		defaultMessage:
			'Page names have to be unique within a space. Give your page a different name to update it.',
	},
	pageTitleInvalidTitleHeader: {
		id: 'editor-flags.page.title.invalid.header',
		description: 'Title for the flag shown when the page title contains only whitespaces',
		defaultMessage: 'Invalid title - try again.',
	},
	pageTitleUpdateInvalidTitleHeader: {
		id: 'editor-flags.update-page.title.invalid.header',
		description: 'Title for the flag shown when the page title contains only whitespaces',
		defaultMessage: 'We can’t update the page with that name.',
	},
	pageTitleInvalidTitleMessage: {
		id: 'editor-flags.page.title.invalid.message',
		description: 'Message for the flag shown when the page title contains only whitespaces',
		defaultMessage: 'Try another title, up to 255 characters.',
	},
	pageTitleTooLongHeader: {
		id: 'editor-flags.title.too.long.title',
		description: 'Title for error flag shown when the page title is too long',
		defaultMessage: 'Your title is too long.',
	},
	pageTitleTooLongMessage: {
		id: 'editor-flags.title.too.long',
		description: 'Message for error flag shown when the page title is too long',
		defaultMessage: 'The limit for titles is 255 characters.',
	},
	duplicateTemplateTitleHeader: {
		id: 'editor-flags.template.can.not.save.error.title.duplicate.title',
		description: "Title for error flag shown when the template's title already exists",
		defaultMessage: 'A template with this name already exists',
	},
	duplicateTemplateTitleMessage: {
		id: 'editor-flags.template.can.not.save.error.title.duplicate',
		description: "Message for error flag shown when the template's title already exists",
		defaultMessage:
			'Template names must be unique within a space. Give your template a different name to save it.',
	},
	pageTooBigHeader: {
		id: 'editor-flags.page.is.too.big.title',
		description: 'Title for error flag shown when the page is too big',
		defaultMessage: 'We can’t save your content',
	},
	pageTooBigMessage: {
		id: 'editor-flags.page.is.too.big',
		description: 'Message for error flag shown when the page is too big',
		defaultMessage:
			'Pages larger than the 2MB limit are slow to load. Break your content into a couple pages and use the Include Page macro to display the content here.',
	},
	templateTooBigHeader: {
		id: 'editor-flags.template.is.too.big.title',
		description: 'Title for error flag shown when the template is too big',
		defaultMessage: 'We can’t save your template',
	},
	templateTooBigMessage: {
		id: 'editor-flags.template.is.too.big',
		description: 'Message for error flag shown when the template is too big',
		defaultMessage:
			'Templates larger than the 2MB limit are slow to load. Break your content into a couple of templates.',
	},
	connectionErrorHeader: {
		id: 'editor-flags.offline.save.error.title',
		description: 'Title for error flag shown when there is a connection error',
		defaultMessage: 'We’re having trouble connecting right now',
	},
	connectionErrorMessage: {
		id: 'editor-flags.offline.save.error',
		description: 'Message for error flag shown on connection errors',
		defaultMessage: 'Give us a few seconds to reconnect, and you can resume your work.',
	},
	templateConnectionParsingFailHeader: {
		id: 'editor-flags.template.can.not.parse.content.title',
		description: 'Title for error flag shown when there is a template parsing error',
		defaultMessage: 'We can’t open this template',
	},
	templateConnectionParsingFailMessage: {
		id: 'editor-flags.template.can.not.parse.content',
		description: 'Message for error flag shown when there is a template parsing error',
		defaultMessage: 'This happens when a template contains broken or unsupported elements.',
	},
	publishIsTakingTooLongHeader: {
		id: 'editor-flags.error.delayed.publishing.title',
		description: 'Title for error flag shown when publishing is taking too long',
		defaultMessage: 'We’re having trouble publishing',
	},
	updateIsTakingTooLongHeader: {
		id: 'editor-flags.error.delayed.updating.title',
		description: 'Title for error flag shown when updating is taking too long',
		defaultMessage: 'We’re having trouble updating',
	},
	publishIsTakingTooLongMessage: {
		id: 'editor-flags.error.delayed.publishing',
		description: 'Message for error flag shown when publishing is taking too long',
		defaultMessage:
			'We’ll continue trying to publish. If it’s taking too long, refresh the page and click {buttonText}.',
	},
	updateIsTakingTooLongMessage: {
		id: 'editor-flags.error.delayed.updating',
		description: 'Message for error flag shown when updating is taking too long',
		defaultMessage:
			'We’ll continue trying to update. If it’s taking too long, refresh the page and click {buttonText}.',
	},
	publishText: {
		id: 'editor-flags.error.delayed.publishText',
		description:
			'The text is shown as a link or button label within an editor error flag when publishing is delayed, representing the publish action that the user can click to retry.',
		defaultMessage: 'Publish',
	},
	updateText: {
		id: 'editor-flags.error.delayed.updateText',
		description:
			'The text is shown as a link or button label within an editor error flag when updating is delayed, representing the update action that the user can click to retry.',
		defaultMessage: 'Update',
	},
	schedulePublishErrorHeader: {
		id: 'editor-flags.error.schedule.publish.title',
		description: 'Title for error flag shown when schedule publish failed',
		defaultMessage: 'We had an issue setting the schedule time',
	},
	schedulePublishErrorMessage: {
		id: 'editor-flags.error.schedule.publish',
		description: 'Message for error flag shown when schedule publish failed',
		defaultMessage: 'Refresh the page and try again.',
	},
	cancelScheduledPublishErrorHeader: {
		id: 'editor-flags.error.cancel.scheduled.publish.title',
		description: 'Title for error flag shown when cancel scheduled publish failed',
		defaultMessage: 'We had an issue cancelling this scheduled publish',
	},
	cancelScheduledUpdateErrorHeader: {
		id: 'editor-flags.error.cancel.scheduled.update.title',
		description: 'Title for error flag shown when cancel scheduled update failed',
		defaultMessage: 'We had an issue cancelling this scheduled update',
	},
	cancelScheduledPublishErrorMessage: {
		id: 'editor-flags.error.cancel.scheduled.publish',
		description: 'Message for error flag shown when cancel scheduled publish failed',
		defaultMessage: 'Refresh the page and try again.',
	},
	schedulePublishNoHeaderErrorHeader: {
		id: 'editor-flags.error.scheduled.publish.no-title-error.title',
		description:
			'Title for error flag shown when user try to schedule publish and there is no page title',
		defaultMessage: 'Your page needs a name',
	},
	schedulePublishNoHeaderErrorMessage: {
		id: 'editor-flags.error.scheduled.publish.no-title-error',
		description:
			'Message for error flag shown when user try to schedule publish and there is no page title',
		defaultMessage: 'Add a title, and you can set a date and time for publishing.',
	},
	scheduleUpdateNoHeaderErrorMessage: {
		id: 'editor-flags.error.scheduled.update.no-title-error',
		description:
			'Message for error flag shown when user try to schedule update and there is no page title',
		defaultMessage: 'Add a title, and you can set a date and time for updating.',
	},
	defaultPublishErrorHeader: {
		id: 'editor-flags.offline.save.error.generic.title',
		description: 'Title for error flag shown on publish errors',
		defaultMessage: `Your work is safe, but there’s an issue`,
	},
	defaultPublishErrorMessage: {
		id: 'editor-flags.offline.save.error.generic',
		description: 'Message for error flag shown on publish errors',
		defaultMessage: 'Refresh the page, and click Publish again.',
	},
	defaultUpdateErrorMessage: {
		id: 'editor-flags.offline.update.save.error.generic',
		description: 'Message for error flag shown on update errors',
		defaultMessage: 'Refresh the page, and click Update again.',
	},
	defaultTemplateSaveErrorHeader: {
		id: 'editor-flags.template.save.error.generic.title',
		description: 'Title for error flag shown on template save errors',
		defaultMessage: `There’s an issue saving your template`,
	},
	defaultTemplateSaveErrorMessage: {
		id: 'editor-flags.template.save.error.generic.message',
		description: 'Message for error flag shown on template save errors',
		defaultMessage: 'Try clicking Save again.',
	},
	duplicateBlogTitleFromEditPageErrorHeader: {
		id: 'editor-flags.error.edit-page.duplicate-blog-title-error.title',
		description: 'Title for error flag shown on publishing a page as a blog',
		defaultMessage: 'A blog with this name already exists',
	},
	duplicateBlogTitleFromEditPageErrorMessage: {
		id: 'editor-flags.error.edit-page.duplicate-blog-title-error.message',
		description: 'Message for error flag shown on publishing a page as a blog',
		defaultMessage: 'Rename the page before publishing it as a blog.',
	},
	duplicateUpdateBlogTitleFromEditPageErrorMessage: {
		id: 'editor-flags.error.edit-page.update.duplicate-blog-title-error.message',
		description: 'Message for error flag shown on updating a page as a blog',
		defaultMessage: 'Rename the page before updating it as a blog.',
	},
	duplicateBlogTitleFromViewPageErrorHeader: {
		id: 'editor-flags.error.view-page.duplicate-blog-title-error.title',
		description: 'Title for error flag shown on converting a page to a blog',
		defaultMessage: 'A blog with this name already exists',
	},
	duplicateBlogTitleFromViewPageErrorMessage: {
		id: 'editor-flags.error.view-page.duplicate-blog-title-error.message',
		description: 'Message for error flag shown on converting a page to a blog',
		defaultMessage: 'Rename the page before converting it to a blog.',
	},
	noAuthorizeMessage: {
		id: 'editor-flags.error.no.authorize.message',
		description:
			'Message for error flag shown when user does not have authorization to a page and tries to publish',
		defaultMessage:
			'Your session has expired. You’ll need to log in again or switch to another account to keep working. {loginLink}',
	},
	loginLink: {
		id: 'editor-flags.error.login.link',
		description: 'Login link so user can switch to another account if they are not authorized',
		defaultMessage: 'Log in or switch to another account',
	},
	noAccessMessage: {
		id: 'editor-flags.error.no.access.message',
		description: 'Message for error flag shown when user does not have access to a page',
		defaultMessage:
			'Your session may have expired. You’ll need to log in again or switch to another account to keep working. {loginLink}',
	},
	pageIsTrashedMessage: {
		id: 'editor-flags.error.page.trashed.message',
		description: 'Message for error flag shown when user edits a page that has been trashed',
		defaultMessage:
			'Copy your content, then add it to a new page or <link>restore this page from the trash</link> and try again.',
	},
	livePageIsTrashedMessage: {
		id: 'editor-flags.error.live.page.trashed.message',
		description: 'Message for error flag shown when user edits a live page that has been trashed',
		defaultMessage:
			'Copy your content, then add it to a new doc or <link>restore this doc from the trash</link> and try again.',
	},
	pageIsTrashedMessageLink: {
		id: 'editor-flags.error.page.trashed.message.link',
		description:
			'Link to view trash for error flag shown when user edits a page that has been trashed',
		defaultMessage: 'Restore this content from the trash',
	},
	invalidTemplateVariableMessage: {
		id: 'editor-flags.error.invalid.template.variable.message',
		description: 'Message for error flag shown when user creates a template with the same name',
		defaultMessage:
			'Multiple template variables which have the same name “{variableNames}” but different parameters.',
	},
	cantRevertPageTitle: {
		id: 'editor.discard-to-published.error.title',
		description: 'Title for when an error occurs while trying to revert a page',
		defaultMessage: 'Can’t revert to last published version',
	},
	cantRevertPageMessage: {
		id: 'editor.discard-to-published.error.message',
		description: 'Message for when an error occurs while trying to revert a page',
		defaultMessage:
			'We can’t get the last published version of the page at the moment. Give it a few moments then try discarding the draft again.',
	},
	parentNotPublishedHeader: {
		id: 'editor-flags.error.edit-page.parent-not-published.title',
		description:
			'Title for error flag shown when the draft cannot be published because the parent page is not published yet',
		defaultMessage: 'This draft can’t be published',
	},
	parentNotPublishedMessage: {
		id: 'editor-flags.error.edit-page.parent-not-published.message',
		description:
			'Message for error flag shown when the draft cannot be published because the parent page is not published yet',
		defaultMessage:
			'A published page can’t exist under a page in the draft state. Publish the <link>parent page</link> first, then publish this page.',
	},
	titleRequiredHeader: {
		id: 'editor-flags.page.title.required.header',
		description: 'Title for the flag shown when the page title is not filled out/empty',
		defaultMessage: 'Title required',
	},
	titleRequiredMessage: {
		id: 'editor-flags.page.title.required.message',
		description: 'Message for the flag shown when the page title is not filled out/empty',
		defaultMessage: 'Add a title before publishing.',
	},
	titleRequiredUpdateMessage: {
		id: 'editor-flags.update.page.title.required.update.message',
		description: 'Message for the flag shown when the page title is not filled out/empty',
		defaultMessage: 'Add a title before updating.',
	},
	titleGeneratedAI: {
		id: 'editor-flags.generate.ai.title',
		defaultMessage: 'Review your AI-generated title',
		description:
			'Text of title from a flag that pops up when AI automatically generates a title for user',
	},
	titleGeneratedAIDescription: {
		id: 'editor-flags.generate.ai.description',
		defaultMessage:
			'Rovo is generating a title for this page, as a title is required to publish. Select <bold>Accept</bold> to publish the page with this title or edit the title and publish manually. <Link>Content quality may vary.</Link>',
		description: 'Description of flag when AI automatically generates a title for user',
	},
	titleGeneratedFromPasteAI: {
		id: 'editor-flags.generate.ai.from.paste.title',
		defaultMessage: 'Rovo wrote a title for you',
		description:
			'Text of title from a flag that pops up when AI automatically generates a title for user from pasted content',
	},
	titleGeneratedFromPasteAIDescription: {
		id: 'editor-flags.generate.ai.from.paste.description',
		defaultMessage: 'It’s based on the text you pasted. <Link>Uses AI. Verify results.</Link>',
		description: 'Description of flag when AI automatically generates a title for user from paste',
	},
	titleGeneratedAIError: {
		id: 'editor-flags.generate.ai.error.title',
		defaultMessage: 'We couldn’t generate a title for you',
		description:
			'Text of title from a flag that pops up when we get an error trying to use AI to automatically generate a title for user',
	},
	titleGeneratedAIErrorDescription: {
		id: 'editor-flags.generate.ai.error.description',
		defaultMessage:
			'Rovo was unable to generate a title for you at this time. You can add a title and publish the page manually instead. <Link>Why did this happen?</Link>',
		description:
			'Description of flag when we get an error trying to use AI to automatically generate a title for user',
	},
	refreshPageConflictHeader: {
		id: 'editor-flags.error.conflict.refresh-page.header',
		description:
			'Title for error flag when there is a conflict in the page version and the page must be refreshed',
		defaultMessage: 'Refresh this page in order to publish',
	},
	refreshPageUpdateConflictHeader: {
		id: 'editor-flags.error.update.conflict.refresh-page.header',
		description:
			'Title for error flag when there is a conflict in the page version and the page must be refreshed',
		defaultMessage: 'Refresh in order to update',
	},
	refreshPageConflictMessage: {
		id: 'editor-flags.error.conflict.refresh-page.message',
		description:
			'Message for error flag when there is a conflict in the page version and the page must be refreshed',
		defaultMessage:
			'This version has been updated by another user. Refresh the page in order to sync and publish your changes.',
	},
	refreshUpdatePageConflictMessage: {
		id: 'editor-flags.error.update.conflict.refresh-page.message',
		description:
			'Message for error flag when there is a conflict in the page version and the page must be refreshed',
		defaultMessage:
			'This version has been updated by another user. Refresh now in order to sync your changes.',
	},
	draftConflictHeader: {
		id: 'editor-flags.error.conflict.draft.header',
		description:
			'Title for error flag when there is a conflict in the draft version and a new draft must be created in order to publish the page',
		defaultMessage: 'Unable to publish draft',
	},
	draftConflictMessage: {
		id: 'editor-flags.error.conflict.draft.message',
		description:
			'Message for error flag when there is a conflict in the draft version and a new draft must be created in order to publish the page',
		defaultMessage:
			'Due to a server error, this draft can’t be published. Copy and paste this content into a new draft in order to publish it.',
	},
	duplicatePageNameWithinEditorErrorTitle: {
		id: 'editor-flags.move-error.duplicate-page-name-within-editor.title',
		defaultMessage: 'Give this page a unique title to move',
		description:
			"Title of instructions in a flag informing a user how they can move their page when they try to move their page to the selected space but the space already contains a single page with their page's name",
	},
	duplicateLivePageNameWithinEditorErrorTitle: {
		id: 'editor-flags.move-error.duplicate-live-page-name-within-editor.title',
		defaultMessage: 'Give this doc a unique title to move',
		description:
			"Title of instructions in a flag informing a user how they can move their live page when they try to move their live page to the selected space but the space already contains a single live page with their live page's name",
	},
	duplicatePageNameWithinEditorErrorDescription: {
		id: 'editor-flags.move-error.duplicate-page-name-within-editor.description',
		defaultMessage: `To move this page to the {destinationSpace} space, give it a unique title and publish it first.`,
		description:
			"Description of instructions in a flag informing a user how they can move their page when they try to move their page to the selected space but the space already contains a single page with their page's name",
	},
	duplicateLivePageNameWithinEditorErrorDescription: {
		id: 'editor-flags.move-error.duplicate-live-page-name-within-editor.description',
		defaultMessage: `To move this live doc to the {destinationSpace} space, give it a unique title.`,
		description:
			"Description of instructions in a flag informing a user how they can move their live doc when they try to move their doc to the selected space but the space already contains a single doc with their doc's name",
	},
	duplicatePageNameWithinEditorUpdateErrorDescription: {
		id: 'editor-flags.move-error.duplicate-page-name-within-editor.update.description',
		defaultMessage: `To move this page to the {destinationSpace} space, give it a unique title and update it first.`,
		description:
			"Description of instructions in a flag informing a user how they can move their page when they try to move their page to the selected space but the space already contains a single page with their page's name",
	},
	duplicateLivePageNameWithinEditorUpdateErrorDescription: {
		id: 'editor-flags.move-error.duplicate-live-page-name-within-editor.update.description',
		defaultMessage: `To move this live doc to the {destinationSpace} space, give it a unique title and update it first.`,
		description:
			"Description of instructions in a flag informing a user how they can move their live doc when they try to move their doc to the selected space but the space already contains a single doc with their doc's name",
	},
	collabStatusErrorTitle: {
		id: 'editor-flags.collab-status-error.title',
		description: 'Title for the flag shown when there is an issue with syncing changes',
		defaultMessage: 'Can’t save changes',
	},
	collabStatusErrorDescription: {
		id: 'editor-flags.collab-status-error.description',
		description:
			'Description of instructions in a flag informinga user to copy work and refresh the page.',
		defaultMessage: 'Copy your work, then refresh, and we’ll try to reconnect.',
	},
	invalidSpaceKeyHeader: {
		id: 'editor-flags.template.save.error.spacekey.invalid.header',
		description:
			'Title for the flag shown when the spaceKey passed when attempting to save a template is invalid.',
		defaultMessage: 'There was an issue creating a template in this space',
	},
	invalidSpaceKeyMessage: {
		id: 'editor-flags.template.save.error.spacekey.invalid.message',
		description:
			'Message for the flag shown when the spaceKey passed when attempting to save a template is invalid.',
		defaultMessage:
			'Check if the space exists and if you have admin permissions to create templates.',
	},
	templateBlankTitleErrorHeader: {
		id: 'editor-flags.template.can.not.save.error.title.blank.header',
		description: "Title for error flag shown when the template's title is blank",
		defaultMessage: 'We can’t save this template',
	},
	templateBlankTitleErrorMessage: {
		id: 'editor-flags.template.can.not.save.error.title.blank.message',
		description: "Message for error flag shown when the template's title is blank",
		defaultMessage: 'Templates can’t have blank titles. Give your template a title to publish.',
	},
});

const i18nLiveDocs = defineMessages({
	pageTooBigMessage: {
		id: 'editor-flags.page.is.too.big.livedocs',
		defaultMessage:
			'Content that is larger than the 2MB limit is slow to load. Break your content into a couple of pages or live docs and use the Include Page macro to display the content here.',
		description: 'Updated version of editor-flags.page.is.too.big',
	},
});

const i18n = mergeLiveDocI18n(i18nBase, i18nLiveDocs);

export type BuildEditorFlagMessageProps = {
	errorData: any;
	statusCode?: number;
	spaceKey?: string;
	nextTryIn?: number;
	parentPageId?: string;
	formatMessage: (...args: any[]) => any;
	isTemplateEditor?: boolean;
	isUnpublishedDraft?: boolean;
	isLivePage?: boolean;
};

export type FlagData = {
	title: string;
	description: JSX.Element | ((...args: any[]) => any) | string;
	details?: string;
};

const BOLD_BUTTON_TEXT = ({ isUnpublishedDraft }: { isUnpublishedDraft: boolean }) => (
	<b>
		<FormattedMessage {...(isUnpublishedDraft ? i18n.publishText : i18n.updateText)} />
	</b>
);

const LOGIN_LINK = () => (
	<div>
		<Link href="/login?application=confluence">
			<FormattedMessage {...i18n.loginLink} />
		</Link>
	</div>
);

const TRASH_LINK = ({ spaceKey, text }: { spaceKey: string; text: React.ReactNode }) => (
	<span>
		<Link href={`${CONTEXT_PATH}/pages/viewtrash.action?key=${spaceKey}`} target="_blank">
			{text}
		</Link>
	</span>
);

const PARENT_LINK = ({ parentPageId, text }: { parentPageId?: string; text: React.ReactNode }) => (
	<span>
		{parentPageId ? (
			<Link
				href={`${CONTEXT_PATH}/pages/resumedraft.action?draftId=${parentPageId}`}
				target="_blank"
			>
				{text}
			</Link>
		) : (
			text
		)}
	</span>
);

const i18nInitializer = (formatMessage, isLivePage) => ({
	DUPLICATE_PAGE_TITLE: (isUnpublishedDraft: boolean) => {
		const duplicatePageTitleMessage =
			// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
			isLivePage && fg('confluence_live_pages_open_beta_trait_opted_in')
				? i18n.duplicateLivePageTitleMessage
				: i18n.duplicatePageTitleMessage;

		return {
			title:
				// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
				isLivePage && fg('confluence_live_pages_open_beta_trait_opted_in')
					? formatMessage(i18n.duplicateLivePageTitleHeader)
					: formatMessage(i18n.duplicatePageTitleHeader),
			description: formatMessage(
				isUnpublishedDraft ? duplicatePageTitleMessage : i18n.duplicateUpdatePageTitleMessage,
			),
		};
	},
	TITLE_TOO_LONG: () => ({
		title: formatMessage(i18n.pageTitleTooLongHeader),
		description: formatMessage(i18n.pageTitleTooLongMessage),
	}),
	DUPLICATE_TEMPLATE_TITLE: () => ({
		title: formatMessage(i18n.duplicateTemplateTitleHeader),
		description: formatMessage(i18n.duplicateTemplateTitleMessage),
	}),
	PARENT_NOT_PUBLISHED: (parentPageId?: string) => ({
		title: formatMessage(i18n.parentNotPublishedHeader),
		description: (
			<FormattedMessage
				{...i18n.parentNotPublishedMessage}
				values={{
					link: (chunks: React.ReactNode[]) => (
						<PARENT_LINK parentPageId={parentPageId} text={chunks} />
					),
				}}
			/>
		),
	}),
	NO_AUTHORIZE: () => ({
		title: formatMessage({
			id: 'editor.offline.save.noauthorized.title',
		}),
		description: (
			<FormattedMessage {...i18n.noAuthorizeMessage} values={{ loginLink: <LOGIN_LINK /> }} />
		),
	}),
	PAGE_CANNOT_BE_ACCESSED: () => ({
		title: formatMessage({
			id: 'editor.page.can.not.be.accessed.title',
		}),
		description: (
			<FormattedMessage {...i18n.noAccessMessage} values={{ loginLink: <LOGIN_LINK /> }} />
		),
	}),
	PAGE_CONFLICT_REFRESH: (isUnpublishedDraft: boolean) => ({
		title: formatMessage(
			isUnpublishedDraft ? i18n.refreshPageConflictHeader : i18n.refreshPageUpdateConflictHeader,
		),
		description: formatMessage(
			isUnpublishedDraft ? i18n.refreshPageConflictMessage : i18n.refreshUpdatePageConflictMessage,
		),
	}),
	PAGE_CONFLICT_DRAFT: () => ({
		title: formatMessage(i18n.draftConflictHeader),
		description: formatMessage(i18n.draftConflictMessage),
	}),
	PAGE_TRASHED: (spaceKey) => ({
		title: formatMessage({ id: 'editor.page.is.trashed.title' }),
		description:
			// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
			isLivePage && fg('confluence_live_pages_open_beta_trait_opted_in') ? (
				<FormattedMessage
					{...i18n.livePageIsTrashedMessage}
					values={{
						link: (chunks) => <TRASH_LINK spaceKey={spaceKey} text={chunks} />,
					}}
				/>
			) : (
				<FormattedMessage
					{...i18n.pageIsTrashedMessage}
					values={{
						link: (chunks) => <TRASH_LINK spaceKey={spaceKey} text={chunks} />,
					}}
				/>
			),
	}),
	PAGE_TOO_BIG: () => ({
		title: formatMessage(i18n.pageTooBigHeader),
		description: formatMessage(i18n.pageTooBigMessage),
	}),
	TEMPLATE_TOO_BIG: () => ({
		title: formatMessage(i18n.templateTooBigHeader),
		description: formatMessage(i18n.templateTooBigMessage),
	}),
	OFFLINE_ERROR: () => ({
		title: formatMessage(i18n.connectionErrorHeader),
		description: formatMessage(i18n.connectionErrorMessage),
	}),
	TEMPLATE_CONTENT_PARSING_FAILURE: () => ({
		title: formatMessage(i18n.templateConnectionParsingFailHeader),
		description: formatMessage(i18n.templateConnectionParsingFailMessage),
	}),
	EMPTY_TITLE: () => ({
		title: formatMessage({ id: 'editor.title.is.empty.title' }),
		description: formatMessage({ id: 'editor.title.is.empty' }),
	}),
	DUPLICATE_EDIT_PAGE_BLOG_TITLE: (isUnpublishedDraft: boolean) => ({
		title: formatMessage(i18n.duplicateBlogTitleFromEditPageErrorHeader),
		description: formatMessage(
			isUnpublishedDraft
				? i18n.duplicateBlogTitleFromEditPageErrorMessage
				: i18n.duplicateUpdateBlogTitleFromEditPageErrorMessage,
		),
	}),
	DUPLICATE_VIEW_PAGE_BLOG_TITLE: () => ({
		title: formatMessage(i18n.duplicateBlogTitleFromViewPageErrorHeader),
		description: formatMessage(i18n.duplicateBlogTitleFromViewPageErrorMessage),
	}),
	MEDIA_FAILED: () => ({
		title: formatMessage({ id: 'editor.cant.publish.changes' }),
		description: formatMessage({ id: 'editor.files.may.be.uploading' }),
	}),
	INVALID_TEMPLATE_VARIABLE: (errorData) => ({
		title: formatMessage({
			id: 'fabric.template.can.not.save.error.title',
		}),
		description: (
			<FormattedMessage
				{...i18n.invalidTemplateVariableMessage}
				values={{ variableNames: errorData.i18nOptions.variableNames }}
			/>
		),
	}),
	DUPLICATE_PAGE_TITLE_WITHIN_EDITOR: (destinationSpace: string, isUnpublishedDraft: boolean) => ({
		title: formatMessage(
			// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
			isLivePage && fg('confluence_live_pages_open_beta_trait_opted_in')
				? i18n.duplicateLivePageNameWithinEditorErrorTitle
				: i18n.duplicatePageNameWithinEditorErrorTitle,
		),
		description:
			// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
			isLivePage && fg('confluence_live_pages_open_beta_trait_opted_in') ? (
				<FormattedMessage
					{...(isUnpublishedDraft
						? i18n.duplicateLivePageNameWithinEditorErrorDescription
						: i18n.duplicateLivePageNameWithinEditorUpdateErrorDescription)}
					values={{ destinationSpace }}
				/>
			) : (
				<FormattedMessage
					{...(isUnpublishedDraft
						? i18n.duplicatePageNameWithinEditorErrorDescription
						: i18n.duplicatePageNameWithinEditorUpdateErrorDescription)}
					values={{ destinationSpace }}
				/>
			),
	}),
	RECONCILIATION_ERROR: (errorData, nextTryIn) => {
		const title = formatMessage({
			id: 'editor.content.reconciliation.error.title',
		});
		if (nextTryIn != null) {
			return {
				title,
				description: (secondsLeft) =>
					formatMessage(
						{ id: 'editor.content.reconciliation.error.retrying' },
						{ 0: `${secondsLeft}` },
					),
			};
		}

		const showStagingError =
			process.env.CLOUD_ENV === 'staging' ||
			process.env.CLOUD_ENV === 'hello' ||
			process.env.CLOUD_ENV === 'branch';

		const messageEnv = showStagingError ? 'staging' : 'prod';
		const i18nDescriptionMessage = {
			id: `editor.content.reconciliation.error.fatal.${messageEnv}`,
		};
		const description = formatMessage(i18nDescriptionMessage);

		const details = `${formatMessage({
			id: 'editor.content.reconciliation.error.details',
		})} ${errorData[ALLOWED_FLAG_MESSAGES.CONTENT_RECONCILIATION]}`;

		return {
			title,
			description,
			details,
		};
	},
	PUBLISH_TIME_TOO_LONG_ERROR: (isUnpublishedDraft: boolean) => ({
		title: formatMessage(
			isUnpublishedDraft ? i18n.publishIsTakingTooLongHeader : i18n.updateIsTakingTooLongHeader,
		),
		description: (
			<FormattedMessage
				{...(isUnpublishedDraft
					? i18n.publishIsTakingTooLongMessage
					: i18n.updateIsTakingTooLongMessage)}
				values={{
					buttonText: <BOLD_BUTTON_TEXT isUnpublishedDraft={isUnpublishedDraft} />,
				}}
			/>
		),
	}),
	SCHEDULE_PUBLISH_ERROR: () => ({
		title: formatMessage(i18n.schedulePublishErrorHeader),
		description: formatMessage(i18n.schedulePublishErrorMessage),
	}),
	CANCEL_SCHEDULE_PUBLISH_ERROR: (isUnpublishedDraft: boolean) => ({
		title: formatMessage(
			isUnpublishedDraft
				? i18n.cancelScheduledPublishErrorHeader
				: i18n.cancelScheduledUpdateErrorHeader,
		),
		description: formatMessage(i18n.cancelScheduledPublishErrorMessage),
	}),
	SCHEDULE_PUBLISH_WITH_NO_TITLE_ERROR: (isUnpublishedDraft: boolean) => ({
		title: formatMessage(i18n.schedulePublishNoHeaderErrorHeader),
		description: formatMessage(
			isUnpublishedDraft
				? i18n.schedulePublishNoHeaderErrorMessage
				: i18n.scheduleUpdateNoHeaderErrorMessage,
		),
	}),
	DEFAULT_PUBLISH_ERROR: (isUnpublishedDraft: boolean) => ({
		title: formatMessage(i18n.defaultPublishErrorHeader),
		description: formatMessage(
			isUnpublishedDraft ? i18n.defaultPublishErrorMessage : i18n.defaultUpdateErrorMessage,
		),
	}),
	DEFAULT_TEMPLATE_SAVE_ERROR: () => ({
		title: formatMessage(i18n.defaultTemplateSaveErrorHeader),
		description: formatMessage(i18n.defaultTemplateSaveErrorMessage),
	}),
	REVERT_CONTENT_ERROR: () => ({
		title: formatMessage(i18n.cantRevertPageTitle),
		description: formatMessage(i18n.cantRevertPageMessage),
	}),
	PUBLISH_CONDITION_ERROR: (errorMsg) => ({
		title: formatMessage(i18n.defaultPublishErrorHeader),
		description: errorMsg,
	}),
	INVALID_PAGE_TITLE_ERROR: (isUnpublishedDraft: boolean) => ({
		title: formatMessage(
			isUnpublishedDraft
				? i18n.pageTitleInvalidTitleHeader
				: i18n.pageTitleUpdateInvalidTitleHeader,
		),
		description: formatMessage(i18n.pageTitleInvalidTitleMessage),
	}),
	TITLE_REQUIRED: (isUnpublishedDraft: boolean) => ({
		title: formatMessage(i18n.titleRequiredHeader),
		description: formatMessage(
			isUnpublishedDraft ? i18n.titleRequiredMessage : i18n.titleRequiredUpdateMessage,
		),
	}),
	AI_GENERATED_TITLE: () => ({
		title: formatMessage(i18n.titleGeneratedAI),
		description: formatMessage(i18n.titleGeneratedAIDescription, {
			Link: (chunks: ReactNode[]) => (
				<Link
					target="_blank"
					href="https://www.atlassian.com/trust/atlassian-intelligence"
					appearance="subtle"
				>
					<u>{chunks}</u>
				</Link>
			),
			bold: (chunks: ReactNode[]) => <Text weight="bold"> {chunks} </Text>,
		}),
	}),
	AI_GENERATED_TITLE_FROM_PASTE: () => ({
		title: formatMessage(i18n.titleGeneratedFromPasteAI),
		description: formatMessage(i18n.titleGeneratedFromPasteAIDescription, {
			Link: (chunks: ReactNode[]) => (
				<Link
					target="_blank"
					href="https://www.atlassian.com/trust/atlassian-intelligence"
					appearance="subtle"
				>
					<u>{chunks}</u>
				</Link>
			),
			bold: (chunks: ReactNode[]) => <Text weight="bold"> {chunks} </Text>,
		}),
	}),
	AI_GENERATED_TITLE_ERROR: () => ({
		title: formatMessage(i18n.titleGeneratedAIError),
		description: formatMessage(i18n.titleGeneratedAIErrorDescription, {
			Link: (chunks: ReactNode[]) => (
				<Link
					target="_blank"
					href="https://support.atlassian.com/organization-administration/docs/troubleshoot-atlassian-intelligence/"
					appearance="subtle"
				>
					<u>{chunks}</u>
				</Link>
			),
		}),
	}),
	COLLAB_STATUS_ERROR: () => ({
		title: formatMessage(i18n.collabStatusErrorTitle),
		description: formatMessage(i18n.collabStatusErrorDescription),
	}),
	INVALID_SPACEKEY_ERROR: () => ({
		title: formatMessage(i18n.invalidSpaceKeyHeader),
		description: formatMessage(i18n.invalidSpaceKeyMessage),
	}),
	TEMPLATE_BLANK_TITLE_ERROR: () => ({
		title: formatMessage(i18n.templateBlankTitleErrorHeader),
		description: formatMessage(i18n.templateBlankTitleErrorMessage),
	}),
});

export const buildEditorFlagMessage = ({
	errorData,
	statusCode,
	spaceKey,
	parentPageId,
	nextTryIn,
	formatMessage,
	isTemplateEditor = false,
	isUnpublishedDraft = true,
	isLivePage = false,
}: BuildEditorFlagMessageProps): FlagData | null => {
	if (!errorData || !Object.keys(errorData).length) {
		return null;
	}
	const i18nFormatter = i18nInitializer(formatMessage, isLivePage);

	// Http errors from the content api when publishing
	if (statusCode != null) {
		switch (statusCode) {
			case 400:
				if (
					errorData[ALLOWED_FLAG_MESSAGES.DUPLICATE_TITLE] ||
					// have to check instead of doing errorData[ALLOWED_FLAG_MESSAGES.DUPLICATE_TITLE_WITH_BAD_EXCEPTION]
					// because BE message would sometimes have extra words at the end of A page with this title already exists
					// eg com.atlassian.confluence.api.service.exceptions.api.BadRequestException: A page with this title already exists:
					// A page already exists with the title <PAGE_TITLE> in the space with key <SPACE_KEY>
					Object.keys(errorData)[0].indexOf(
						ALLOWED_FLAG_MESSAGES.DUPLICATE_TITLE_WITH_BAD_EXCEPTION,
					) !== -1
				) {
					return i18nFormatter.DUPLICATE_PAGE_TITLE(isUnpublishedDraft);
				} else if (errorData[ALLOWED_FLAG_MESSAGES.INVALID_PAGE_TITLE]) {
					return i18nFormatter.INVALID_PAGE_TITLE_ERROR(isUnpublishedDraft);
				} else if (errorData[ALLOWED_FLAG_MESSAGES.TITLE_TOO_LONG]) {
					return i18nFormatter.TITLE_TOO_LONG();
				} else if (
					Object.keys(errorData)[0].indexOf(ALLOWED_FLAG_MESSAGES.DUPLICATE_TITLE_TEMPLATE) !== -1
				) {
					return i18nFormatter.DUPLICATE_TEMPLATE_TITLE();
				} else if (errorData[ALLOWED_FLAG_MESSAGES.PARENT_NOT_PUBLISHED]) {
					return i18nFormatter.PARENT_NOT_PUBLISHED(parentPageId);
				} else if (errorData[ALLOWED_FLAG_MESSAGES.TEMPLATE_INVALID_SPACEKEY_ERROR]) {
					return i18nFormatter.INVALID_SPACEKEY_ERROR();
				} else if (errorData[ALLOWED_FLAG_MESSAGES.TEMPLATE_BLANK_TITLE_ERROR]) {
					return i18nFormatter.TEMPLATE_BLANK_TITLE_ERROR();
				} else if (
					// BadRequestException means something wrong with the request. BE throws this exception on request validation fail
					// This BadRequestException has a message attached to it and we should use it as default message if non of above conditions are met.
					// That's why make sure this "if" block should say at last.
					// We are adding this as part of our PublishCondition implementation. Please go through: https://hello.atlassian.net/wiki/spaces/CCECO/pages/1289257589/Custom+publishing+flow
					// For any further questions please contact ecosystem-ini team.
					Object.keys(errorData)[0].indexOf(
						ALLOWED_FLAG_MESSAGES.ERROR_MESSAGE_WITH_BAD_EXCEPTION,
					) !== -1
				) {
					// positive look-behind "(?<=expression)" is not supported with str.match() so end up using below pattern.
					const patt =
						'[^com.atlassian.confluence.api.service.exceptions.api.BadRequestException:].*';
					const errorMsg = Object.keys(errorData)[0].match(patt);
					return i18nFormatter.PUBLISH_CONDITION_ERROR(errorMsg);
				}
				break;
			case 403:
				return i18nFormatter.NO_AUTHORIZE();
			case 404:
				return i18nFormatter.PAGE_CANNOT_BE_ACCESSED();
			case 409:
				if (
					Object.keys(errorData)[0].indexOf(ALLOWED_FLAG_MESSAGES.CONFLICT_DRAFT_VERSIONING) !== -1
				) {
					return i18nFormatter.PAGE_CONFLICT_DRAFT();
				}
				return i18nFormatter.PAGE_CONFLICT_REFRESH(isUnpublishedDraft);
			case 410:
				if (isTemplateEditor) {
					return i18nFormatter.DEFAULT_TEMPLATE_SAVE_ERROR();
				}
				return i18nFormatter.PAGE_TRASHED(spaceKey);
			case 413:
				return isTemplateEditor ? i18nFormatter.TEMPLATE_TOO_BIG() : i18nFormatter.PAGE_TOO_BIG();
			case 0:
			case 500:
			case 503:
				return i18nFormatter.OFFLINE_ERROR();
		}
	}

	if (errorData[ALLOWED_FLAG_MESSAGES.TEMPLATE_CONTENT_PARSING_FAILURE]) {
		return i18nFormatter.TEMPLATE_CONTENT_PARSING_FAILURE();
	}

	// editor errors triggered from within the fabric editor
	if (errorData[ALLOWED_FLAG_MESSAGES.EMPTY_TITLE]) {
		return i18nFormatter.EMPTY_TITLE();
	}

	if (errorData[ALLOWED_FLAG_MESSAGES.DUPLICATE_VIEW_PAGE_BLOG_TITLE]) {
		return i18nFormatter.DUPLICATE_VIEW_PAGE_BLOG_TITLE();
	}

	if (errorData[ALLOWED_FLAG_MESSAGES.DUPLICATE_TITLE_WITHIN_EDITOR]) {
		return i18nFormatter.DUPLICATE_PAGE_TITLE_WITHIN_EDITOR(
			errorData.destinationSpace,
			isUnpublishedDraft,
		);
	}

	if (errorData[ALLOWED_FLAG_MESSAGES.DUPLICATE_EDIT_PAGE_BLOG_TITLE]) {
		return i18nFormatter.DUPLICATE_EDIT_PAGE_BLOG_TITLE(isUnpublishedDraft);
	}

	if (errorData[ALLOWED_FLAG_MESSAGES.DUPLICATE_TITLE_TEMPLATE]) {
		return i18nFormatter.DUPLICATE_TEMPLATE_TITLE();
	}

	if (errorData[ALLOWED_FLAG_MESSAGES.MEDIA_FAILED]) {
		return i18nFormatter.MEDIA_FAILED();
	}

	if (errorData[ALLOWED_FLAG_MESSAGES.FOUND_INVALID_TEMPLATE_VARIABLE]) {
		return i18nFormatter.INVALID_TEMPLATE_VARIABLE(errorData);
	}

	if (errorData[ALLOWED_FLAG_MESSAGES.CONTENT_RECONCILIATION]) {
		return i18nFormatter.RECONCILIATION_ERROR(errorData, nextTryIn);
	}

	if (errorData[ALLOWED_FLAG_MESSAGES.PUBLISH_TIME_TOO_LONG_ERROR]) {
		return i18nFormatter.PUBLISH_TIME_TOO_LONG_ERROR(isUnpublishedDraft);
	}

	if (errorData[ALLOWED_FLAG_MESSAGES.SCHEDULE_PUBLISH_ERROR]) {
		return i18nFormatter.SCHEDULE_PUBLISH_ERROR();
	}

	if (errorData[ALLOWED_FLAG_MESSAGES.CANCEL_SCHEDULE_PUBLISH_ERROR]) {
		return i18nFormatter.CANCEL_SCHEDULE_PUBLISH_ERROR(isUnpublishedDraft);
	}

	if (errorData[ALLOWED_FLAG_MESSAGES.SCHEDULE_PUBLISH_WITH_NO_TITLE_ERROR]) {
		return i18nFormatter.SCHEDULE_PUBLISH_WITH_NO_TITLE_ERROR(isUnpublishedDraft);
	}

	if (errorData[ALLOWED_FLAG_MESSAGES.REVERT_CONTENT_ERROR]) {
		return i18nFormatter.REVERT_CONTENT_ERROR();
	}

	// UX https://product-fabric.atlassian.net/browse/CEMS-2468
	if (errorData[ALLOWED_FLAG_MESSAGES.COLLAB_INIT_TIMEOUT_TAKING_TOO_LONG]) {
		return i18nFormatter.OFFLINE_ERROR();
	}

	if (errorData[ALLOWED_FLAG_MESSAGES.TITLE_REQUIRED]) {
		return i18nFormatter.TITLE_REQUIRED(isUnpublishedDraft);
	}

	if (errorData[ALLOWED_FLAG_MESSAGES.AI_GENERATED_TITLE]) {
		return i18nFormatter.AI_GENERATED_TITLE();
	}

	if (errorData[ALLOWED_FLAG_MESSAGES.AI_GENERATED_TITLE_FROM_PASTE]) {
		return i18nFormatter.AI_GENERATED_TITLE_FROM_PASTE();
	}

	if (errorData[ALLOWED_FLAG_MESSAGES.AI_GENERATED_TITLE_ERROR]) {
		return i18nFormatter.AI_GENERATED_TITLE_ERROR();
	}

	// Adding duplicate and invalid page errors outside of status code 400 for use with publish modal buttons (they do not send requests)
	if (
		errorData[ALLOWED_FLAG_MESSAGES.DUPLICATE_TITLE] ||
		// have to check instead of doing errorData[ALLOWED_FLAG_MESSAGES.DUPLICATE_TITLE_WITH_BAD_EXCEPTION]
		// because BE message would sometimes have extra words at the end of A page with this title already exists
		// eg com.atlassian.confluence.api.service.exceptions.api.BadRequestException: A page with this title already exists:
		// A page already exists with the title <PAGE_TITLE> in the space with key <SPACE_KEY>
		Object.keys(errorData)[0].indexOf(ALLOWED_FLAG_MESSAGES.DUPLICATE_TITLE_WITH_BAD_EXCEPTION) !==
			-1
	) {
		return i18nFormatter.DUPLICATE_PAGE_TITLE(isUnpublishedDraft);
	}
	if (errorData[ALLOWED_FLAG_MESSAGES.INVALID_PAGE_TITLE]) {
		return i18nFormatter.INVALID_PAGE_TITLE_ERROR(isUnpublishedDraft);
	}

	if (isTemplateEditor) {
		return i18nFormatter.DEFAULT_TEMPLATE_SAVE_ERROR();
	}

	if (errorData[ALLOWED_FLAG_MESSAGES.COLLAB_STATUS_ERROR]) {
		return i18nFormatter.COLLAB_STATUS_ERROR();
	}

	return i18nFormatter.DEFAULT_PUBLISH_ERROR(isUnpublishedDraft);
};
